import { useState } from 'react';
import { useLocation } from 'react-router-dom';

import { LoginRequiredSheet } from '@/components/shared/LoginRequiredSheet';
import { useInstitutionRoles } from '@/compositions/hooks/useInstitutionRoles';
import { useSession } from '@/compositions/hooks/useSession';
import { useUnreadNoticesCount } from '@/compositions/hooks/useUnreadNoticesCount';
import { getNavigationItems, isDestinationRoute } from '@/config/navigation';
import { cn } from '@/utils/helpers/cn';

import { AppMenuTab } from './AppMenu';
import { BottomNavLink } from './BottomNavItem';

/**
 * Barra de navegação inferior.
 *
 * Fica no rodapé porque é a zona alcançável pelo polegar em telefone — o mesmo
 * motivo pelo qual docs/07-mobile-e-pwa.md §8 põe o banner de atualização ali.
 *
 * Os itens vêm de `config/navigation.ts`, que registra que a composição da
 * barra **ainda não está decidida**. Aqui mora só a estrutura.
 */
/**
 * Aba que recebe o contador de avisos pessoais.
 *
 * A rota está escrita aqui, e não em `NAVIGATION_ITEMS`: o badge é estado de
 * render (muda a cada ping), não configuração de rota — pôr um campo de
 * contagem no item de navegação faria a config carregar um dado que ela não
 * tem como saber.
 */
const NOTICES_ROUTE = '/alerts';

export function BottomNav({ className }: { className?: string }) {
  const { isAuthenticated, isLoading } = useSession();
  const [gatedPath, setGatedPath] = useState<string | null>(null);
  const { pathname } = useLocation();
  // Convidado não tem notificação: o hook nem consulta sem sessão e devolve 0,
  // então a barra do convidado não desenha badge nenhum.
  const unreadNotices = useUnreadNoticesCount();
  // Conta de QG troca "Pedidos" pelo atalho do mapa da operação (T26).
  const roles = useInstitutionRoles();
  const navigationItems = getNavigationItems(roles);

  // Trocar de aba não empilha (`config/navigation.ts`): saindo de uma
  // tela-destino, a navegação substitui a entrada atual e o nível de destinos
  // fica com uma entrada só. Sem isso, Mapa → Avisos → Mapa deixava três
  // degraus e o "voltar" refazia o caminho tela por tela.
  const isOnDestination = isDestinationRoute(pathname, roles);

  // O gate só intercepta o item de destaque (SOS): as demais abas são
  // públicas, e o menu é um painel que o convidado também pode abrir. Enquanto a sessão
  // carrega, ninguém é bloqueado — a rota destino tem o próprio guard.
  const shouldGateEmphasis = !isAuthenticated && !isLoading;

  return (
    <nav
      // Âncora do toast: `src/index.css` usa `:has([data-bottom-nav])` para
      // assentá-lo acima da barra (docs/07 §4, "Toast").
      data-bottom-nav=""
      aria-label="Navegação principal"
      className={cn(
        'bg-surface border-border fixed inset-x-0 bottom-0 z-40 border-t',
        // A barra desenha até o fim da tela, mas o conteúdo respeita o inset —
        // sem isso os rótulos ficam sob a barra de gestos do iPhone.
        'pb-[env(safe-area-inset-bottom,0px)] px-1',
        className,
      )}
    >
      {/* `overflow-visible` é o que deixa o botão de socorro passar da barra.
          Qualquer `overflow-hidden` num ancestral o corta pela metade. */}
      {/*
        Grid e não flex, com trilhas `1fr 1fr 1.25fr 1fr 1fr`: a coluna central
        recebe um quarto a mais para dar folga ao círculo de 44px do Socorro,
        sem estreitar demais os vizinhos — em 390px isso dá ~92px ao centro e
        ~74px a cada lateral.

        Cada trilha é `minmax(0, …)` escrita à mão, e não `1fr` puro: `1fr`
        sozinho é `minmax(auto, 1fr)`, cujo mínimo `auto` impede a coluna de
        encolher abaixo do próprio conteúdo. É o mesmo mínimo do
        `min-width: auto` do flex — e foi ele que fez o rótulo mais longo
        ("Notificações") alargar a célula e empurrar as vizinhas: cada célula
        com 100px em vez de 78px, total em 500px numa tela de 390px, o SOS 55px
        fora do centro e "Perfil" para fora da tela.

        `overflow-visible` é o que deixa o botão de socorro passar da barra.
      */}
      <ul className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.25fr)_minmax(0,1fr)_minmax(0,1fr)] items-end overflow-visible pb-2">
        {navigationItems.map((item) => (
          <li key={item.to} className="flex min-w-0 justify-center">
            <BottomNavLink
              item={item}
              // O destino também precisa ser um destino, e não só a origem: o
              // SOS é a única aba que leva a um fluxo de tarefa fora do
              // `AppLayout`. Substituir a entrada ao entrar nele faria o
              // "voltar" de dentro do socorro sair do app em vez de devolver a
              // tela de onde a pessoa pediu ajuda.
              replace={isOnDestination && isDestinationRoute(item.to, roles)}
              unreadCount={item.to === NOTICES_ROUTE ? unreadNotices : undefined}
              onGuestBlocked={item.emphasis && shouldGateEmphasis ? setGatedPath : undefined}
            />
          </li>
        ))}
        {/*
          A última célula é o menu, e não uma rota: menu e perfil trocaram de
          lugar (planilha, 2026-10) — o perfil subiu para o cabeçalho, onde
          ficava o hambúrguer, e o menu desceu para cá. Ele fica **fora** de
          `NAVIGATION_ITEMS` porque abre um painel em vez de navegar; a régua da
          célula é a mesma (`BottomNavItem.tsx`).
        */}
        <li className="flex min-w-0 justify-center">
          <AppMenuTab />
        </li>
      </ul>

      <LoginRequiredSheet
        open={gatedPath !== null}
        onOpenChange={(open) => {
          if (!open) setGatedPath(null);
        }}
        next={gatedPath ?? undefined}
        description="Para pedir socorro você precisa de uma conta — é ela que permite acompanhar o resgate."
      />
    </nav>
  );
}
